import { useState } from "react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useRefreshDirectory } from "@/lib/queries";
import { lots } from "@/lib/format";
import type { DeletionPlan, DeletionResult, DeletionScope } from "@/lib/types";

/**
 * Le entita' che si cancellano, e dove chiedere.
 *
 * La mappa sta qui e non nei chiamanti: una pagina che costruisse da se' l'URL
 * dell'anteprima e quello della cancellazione potrebbe sbagliarne uno, e i due
 * devono parlare della stessa riga.
 */
const ROTTE = {
  cliente: (id: string) => `/clients/${id}`,
  conto: (id: string) => `/accounts/${id}`,
  fase: (id: string) => `/sessions/${id}`,
  ciclo: (id: string) => `/cycles/${id}`,
  server: (id: string) => `/servers/${id}`,
} as const;

export type DeletableKind = keyof typeof ROTTE | "simbolo";

/**
 * Conto, ciclo e fase non si cancellano: si timbrano, si provano a chiudere le
 * posizioni a mercato, e si ripristinano dagli Eliminati. Non hanno blocchi.
 * Cliente e simbolo restano cancellazioni vere, con i loro blocchi.
 */
const SI_TIMBRA = new Set<DeletableKind>(["conto", "ciclo", "fase"]);

/**
 * ELIMINA DEFINITIVAMENTE, con la conseguenza dichiarata PRIMA.
 *
 * Non «attenzione, azione irreversibile»: **la frase che dice che cosa succede a
 * questo dato**, coi numeri dentro, presa dal backend e non riscritta qui. Se la
 * frase la scrivesse l'interfaccia, il giorno in cui la regola cambia
 * l'interfaccia direbbe una cosa e il backend ne farebbe un'altra.
 *
 * Tre stati, e il primo è quello che conta:
 *
 *  1. **non si è ancora chiesto**: solo il pulsante. L'anteprima costa una
 *     lettura e una domanda al gateway — non si fa a ogni riga di una tabella,
 *     si fa quando qualcuno ci pensa davvero;
 *  2. **si è chiesto e non si può**: i motivi, uno per uno. Non «impossibile»;
 *  3. **si è chiesto e si può**: la frase, le conseguenze, e la conferma.
 *
 * È diverso da «Archivia», che sta accanto: archiviare conserva i riferimenti,
 * questa li toglie di mezzo. Serve per le righe nate per errore — e serve
 * perché archiviarle **non libererebbe lo slot unico**: un conto archiviato
 * tiene il suo numero MT5, e quel numero non si può reinserire.
 */
export function DeleteForever({
  kind,
  id,
  label,
  size,
  serverId,
  onDeleted,
}: {
  kind: DeletableKind;
  id: string;
  label: string;
  size?: "sm";
  /** Solo per `simbolo`: la mappatura vive sotto il suo server. */
  serverId?: string;
  onDeleted?: () => void;
}) {
  const refresh = useRefreshDirectory();
  const qc = useQueryClient();
  const [plan, setPlan] = useState<DeletionPlan | DeletionScope | null>(null);
  const [esito, setEsito] = useState<DeletionResult | null>(null);
  const [capito, setCapito] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const base = kind === "simbolo" ? `/servers/${serverId}/symbols/${id}` : ROTTE[kind](id);
  // Il simbolo si cancella sulla sua stessa rotta; le altre entita' hanno un
  // `/permanent` che le distingue dall'archiviazione.
  const urlCancella = kind === "simbolo" ? base : `${base}/permanent`;

  const chiedi = async () => {
    setBusy(true);
    setError(null);
    try {
      setCapito(false);
      setPlan(await api.deletionPreview<DeletionPlan | DeletionScope>(`${base}/deletion-preview`));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancella = async () => {
    setBusy(true);
    setError(null);
    try {
      if (SI_TIMBRA.has(kind)) {
        // L'esito resta a video finche' non lo si chiude, e l'aggiornamento
        // dell'elenco aspetta: la riga sparirebbe portandosi via il riquadro.
        setEsito(await api.deleteAt<DeletionResult>(urlCancella));
        setPlan(null);
        return;
      }
      await api.deleteAt(urlCancella);
      setPlan(null);
      await refresh();
      onDeleted?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const chiudiEsito = async () => {
    setEsito(null);
    await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ["deleted"] }), qc.invalidateQueries({ queryKey: ["archived"] })]);
    onDeleted?.();
  };

  if (esito) {
    const rimaste = esito.closures.filter((c) => c.esito === "RIMASTA_APERTA");
    const rosso = rimaste.length > 0;
    return (
      <div role={rosso ? "alert" : "status"} className={`rounded border p-3 text-left ${rosso ? "border-loss bg-loss/10" : "border-line bg-raised/40"}`}>
        <p className={`text-sm font-medium ${rosso ? "text-loss" : "text-ink"}`}>
          {rosso
            ? `Eliminato, ma ${rimaste.length === 1 ? "1 posizione è rimasta aperta" : `${rimaste.length} posizioni sono rimaste aperte`} a mercato: evento critico aperto.`
            : `Eliminato ${label}.`}
        </p>
        {esito.closures.length > 0 ? (
          <ul className="mt-1.5 space-y-0.5">
            {esito.closures.map((c) => (
              <li key={`${c.accountLabel}-${c.ticket}`} className={`text-xs ${c.esito === "RIMASTA_APERTA" ? "text-loss" : "text-muted"}`}>
                {c.accountLabel} · #{c.seq} · {c.symbol} {lots(c.lots)} lotti · ticket <span className="font-mono">{c.ticket}</span>:{" "}
                {c.esito === "CHIUSA" ? "chiusa" : `rimasta aperta${c.motivo ? ` (${c.motivo})` : ""}`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1.5 text-xs text-muted">Nessuna posizione nostra a mercato.</p>
        )}
        {esito.unverified.length > 0 && (
          <ul className="mt-1.5 space-y-0.5">
            {esito.unverified.map((u) => (
              <li key={u.label} className="text-xs text-warn">
                {u.label}: non verificabile{u.motivo ? ` (${u.motivo})` : ""}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3">
          <Button size="sm" variant="ghost" onClick={() => void chiudiEsito()}>
            Chiudi
          </Button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <span className="inline-flex items-center gap-2">
        <Button size={size} variant="ghost" disabled={busy} onClick={() => void chiedi()}>
          {busy ? "Verifico…" : "Elimina"}
        </Button>
        {error && (
          <span role="alert" className="text-xs text-loss">
            {error}
          </span>
        )}
      </span>
    );
  }

  const bloccato = "blockers" in plan && plan.blockers.length > 0;
  const aMercato = "openLegs" in plan ? plan.openLegs : [];

  return (
    <div className="rounded border border-line bg-raised/40 p-3 text-left">
      <p className={`text-sm ${bloccato ? "text-ink" : "font-medium text-ink"}`}>{plan.sentence}</p>

      {bloccato ? (
        <ul className="mt-1.5 space-y-0.5">
          {("blockers" in plan ? plan.blockers : []).map((b) => (
            <li key={b.code} className="text-xs text-loss">
              {b.message}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {/* Le posizioni a mercato si mostrano sotto, una per riga coi lotti: qui non si ripetono. */}
          {plan.consequences
            .filter((c) => c.code !== "WILL_CLOSE")
            .map((c) => (
              <li key={c.code} className="text-xs text-muted">
                {c.label}
              </li>
            ))}
        </ul>
      )}

      {aMercato.length > 0 && (
        <div className="mt-2 rounded border border-warn/50 p-2">
          <ul className="space-y-0.5">
            {aMercato.map((l) => (
              <li key={l.legId} className="text-xs text-ink">
                {l.accountLabel} · #{l.seq} · {l.symbol} {lots(l.lots)} lotti · ticket <span className="font-mono">{l.ticket}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-xs text-muted">
            Verrà tentata la chiusura a mercato; quello che non si chiude resta aperto e segnalato come critico.
          </p>
          <label className="mt-2 flex items-center gap-2 text-xs text-ink">
            <input type="checkbox" checked={capito} disabled={busy} onChange={(e) => setCapito(e.target.checked)} />
            Ho capito
          </label>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!bloccato && (
          <Button size="sm" variant="danger" disabled={busy || (aMercato.length > 0 && !capito)} onClick={() => void cancella()}>
            {busy ? "Elimino…" : aMercato.length > 0 ? `Elimina ${label}` : `Ho capito: elimina ${label}`}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPlan(null)}>
          {bloccato ? "Chiudi" : "Annulla"}
        </Button>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-loss">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * ARCHIVIA un conto. **La meta' conservativa della scelta.**
 *
 * La rotta c'era dal primo giorno e **nessun componente la chiamava**: una
 * funzione che esiste e che nessuno puo' usare, esattamente come il «Ripristina»
 * prima del passo 16.
 *
 * E da quando accanto c'e' «Elimina» e' peggio: l'operatore vedrebbe **solo
 * l'opzione distruttiva** di una coppia in cui quella conservativa esiste ma
 * passa solo dall'API. Sono le due meta' di una scelta, e mostrarne una sola la
 * fa sembrare l'unica.
 *
 * L'archiviazione non chiede un'anteprima: non c'e' niente da dichiarare —
 * niente sparisce, e si torna indietro dalla sezione «Archiviati». Il backend
 * rifiuta comunque se ci sono soldi a mercato (`marketBlocker`), e quel rifiuto
 * si mostra com'e' arrivato.
 */
export function ArchiveAccount({ id, label, size }: { id: string; label: string; size?: "sm" }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const archivia = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.accounts.archive(id);
      setConfirm(false);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {confirm ? (
        <>
          <Button size={size} variant="danger" disabled={busy} onClick={() => void archivia()}>
            {busy ? "Archivio…" : `Conferma: archivia ${label}`}
          </Button>
          <Button size={size} variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
            Annulla
          </Button>
          <span className="text-xs text-faint">Sparisce dagli elenchi, non dal database: si ripristina da Clienti.</span>
        </>
      ) : (
        <Button size={size} variant="ghost" onClick={() => setConfirm(true)}>
          Archivia
        </Button>
      )}
      {error && (
        <span role="alert" className="text-xs text-loss">
          {error}
        </span>
      )}
    </span>
  );
}
